import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChartConfig,
  ChartDateRange,
  ChartId,
  QueryResponse,
  DashboardChart,
  DimensionId,
  KpiId,
  resolveComparison,
  resolveDateRangeControl,
  withDerivedTotals
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { awaitJSON, DashboardingLambdaFetch } from '../../../utils/fetch-utils';
import { FetchError } from '../../../utils/fetch-error';
import {
  applyBreakdown,
  applyComparisonDateRange,
  applyDateRange,
  applyKpi
} from '../../Dashboard/date-range';
import { ChartHandle, isDraftId } from '../chart-handle';

export interface ChartWithData {
  config: ChartConfig;
  data: QueryResponse;
  // The filters SENT with this response; its statuses index into this list, not the current filters.
  dashboardFilters: FilterNode[];
}

interface ChartDataOverrides {
  dateRange?: ChartDateRange;
  comparisonRange?: ChartDateRange;
  // Whether the dates above were chosen for THIS chart specifically — a per-card picker, or a
  // focused-view URL param — rather than inherited from the dashboard's global picker. A chart
  // that declares no `dateRange` control ignores the dashboard's date, but an explicit choice made
  // against that one chart still applies: the declaration says "I don't follow the dashboard", not
  // "my window can never be changed".
  dateRangeIsExplicit?: boolean;
  kpiId?: KpiId;
  // The viewer's breakdown pick. The request is grouped by it alone (else the control's default), so
  // every pick is its own fetch — see prepareChartRequest.
  breakdown?: DimensionId;
  // The dashboard's filter tree, and the dimensions this chart opts out of it for. Both are part of
  // the cache key and of the /getData body (see dashboardFilterRequestFields).
  dashboardFilters?: FilterNode[];
  ignoreDashboardFilters?: DimensionId[];
}

// The /getData body fields for the dashboard's filters. Empty when there are none, so an unfiltered
// request carries no filter fields.
export const dashboardFilterRequestFields = ({
  dashboardFilters,
  ignoreDashboardFilters
}: ChartDataOverrides = {}): Pick<
  ChartDataOverrides,
  'dashboardFilters' | 'ignoreDashboardFilters'
> => {
  if (!dashboardFilters?.length) return {};
  return {
    dashboardFilters,
    ...(ignoreDashboardFilters?.length ? { ignoreDashboardFilters } : {})
  };
};

// Matches every override variant of one chart's render query.
export const chartDataKeyPrefix = (company: string, chartId: ChartHandle) => [
  'dashboarding-chart',
  company,
  chartId
];

// The render query's cache key. Normalized so key identity is stable regardless of object key order.
// Includes a hash of `baseConfig` (the working copy) so two different working copies for the same
// (company, chartId, overrides) can't collide on each other's cached data (a `null` slot when the
// chart renders from its saved row). Exported so the single-chart view's exits can seed this exact
// entry after a save, avoiding a redundant /getChart + /getData reload.
export const buildChartDataQueryKey = (
  company: string,
  chartId: ChartHandle,
  overrides?: ChartDataOverrides,
  baseConfig?: ChartConfig
) => [
  ...chartDataKeyPrefix(company, chartId),
  JSON.stringify({
    dateRange: overrides?.dateRange ?? null,
    comparisonRange: overrides?.comparisonRange ?? null,
    kpiId: overrides?.kpiId ?? null,
    breakdown: overrides?.breakdown ?? null,
    // Absent when there are no filters, so an unfiltered request's key has no filter fields.
    ...dashboardFilterRequestFields(overrides)
  }),
  baseConfig ? JSON.stringify(baseConfig) : null
];

// Rate limits and timeouts arrive as 4xx but mean "not now", not "never" — a dashboard firing a
// request per card can have a few of them clipped by a burst that passes in seconds. fetch-utils
// already groups these with 5xx as transient; this keeps that classification.
const TRANSIENT_STATUSES = new Set([408, 429]);

// A 4xx is otherwise a verdict, not a hiccup: a chart that was deleted 404s on every attempt, so
// retrying only doubles the request count before showing the same failure. Everything else — 5xx,
// transport errors, and the transient statuses above — keeps the app-wide single retry (see the
// QueryClient defaults in main.tsx).
export const retryUnlessClientError = (failureCount: number, error: Error) => {
  const isVerdict =
    error instanceof FetchError &&
    error.status >= 400 &&
    error.status < 500 &&
    !TRANSIENT_STATUSES.has(error.status);
  return !isVerdict && failureCount < 1;
};

// Both chart queries opt OUT of React Query's retry-on-mount, and the reason is not obvious.
//
// A dashboard card renders inside ChartContainer, which swaps its children for a spinner while
// loading — so the component that OWNS the query unmounts the moment the query starts, and remounts
// when it settles. React Query refetches on mount for any query that has never successfully loaded:
// `shouldLoadOnMount` is true whenever `data === undefined`, and `retryOnMount` defaults to true.
// (The app-wide `refetchOnMount: false` does not help — it only governs queries that already HAVE
// data.) Those two behaviors form a hot loop for a chart that cannot load: fetch → spinner →
// unmount → fail → remount → fetch, measured at roughly one request per card per second against a
// chart that 404s, with the card stuck on a spinner because each new fetch resets the query to
// `pending` before the error state can render.
//
// Opting out makes a failure terminal: the card settles, shows why, and stops asking.
const FAILURE_IS_TERMINAL = {
  retryOnMount: false,
  retry: retryUnlessClientError
} as const;

// A chart only follows the DASHBOARD's date picker if it declares a `dateRange` control
// (`resolveDateRangeControl` is `undefined` when it does not) — one that never opted in keeps its
// own saved/pinned ranges. That is about the dashboard's date, not about the chart being frozen:
// a date aimed at this one chart (`dateRangeIsExplicit`) still applies either way.
//
// A period-over-period chart must NOT go through applyDateRange — that overwrites every query with
// one range, collapsing the base and comparison periods into one. Route it through
// applyComparisonDateRange instead (base -> q0, comparison -> q1); a period with no override keeps
// its saved window.
export const applyDateRangeOverrides = (
  config: ChartConfig,
  overrides?: Pick<ChartDataOverrides, 'dateRange' | 'comparisonRange' | 'dateRangeIsExplicit'>
): ChartConfig => {
  // Either the chart opted into date picking, or the viewer aimed a date at this chart on purpose.
  const followsPicker =
    Boolean(resolveDateRangeControl(config)) || Boolean(overrides?.dateRangeIsExplicit);
  if (resolveComparison(config)) {
    if (followsPicker && (overrides?.dateRange || overrides?.comparisonRange)) {
      return applyComparisonDateRange(config, overrides.dateRange, overrides.comparisonRange);
    }
    return config;
  }
  if (followsPicker && overrides?.dateRange) {
    return applyDateRange(config, overrides.dateRange);
  }
  return config;
};

/**
 * Apply the viewer's overrides to a chart config and split it into the two configs a fetch needs.
 *
 * - `config` is what the viewer SEES: the saved config with the date / KPI overrides applied (the
 *   breakdown goes into `fetchConfig` only). It is returned from `useChartData` and read by the
 *   tooltip and renderers, so it carries no derivation (the totals query keeps its own, unfiltered
 *   shape).
 * - `fetchConfig` is what is SENT to `/getData`: `config` narrowed to the active breakdown (the other
 *   breakdown options dropped from the grouping — `applyBreakdown`), and the totals query given the
 *   detail query's metric filters (tagged with a rollup grain). Both are transport details: `config`
 *   keeps every option grouped, because it is what a save or the agent's focus context builds on, and
 *   the validator requires every breakdown option in a query.
 *
 * Neither input nor output is mutated.
 */
export const prepareChartRequest = (
  config: ChartConfig,
  overrides?: ChartDataOverrides
): { config: ChartConfig; fetchConfig: ChartConfig } => {
  let viewConfig = applyDateRangeOverrides(config, overrides);
  if (overrides?.kpiId) {
    viewConfig = applyKpi(viewConfig, overrides.kpiId);
  }
  return {
    config: viewConfig,
    fetchConfig: withDerivedTotals(applyBreakdown(viewConfig, overrides?.breakdown))
  };
};

// The envelope query's cache key. Exported and built HERE so the string exists in exactly one place —
// before this, the same literal was typed out in three files and had to be kept in step by hand.
export const chartEnvelopeKey = (company: string, chartId: ChartHandle | null) => [
  'dashboarding-chart-envelope',
  company,
  chartId
];

// Query options for a saved chart's whole ENVELOPE by id (no compile/run, no view overrides).
//
// /getChart already returns the envelope — the config PLUS createdBy and the two role levels. This
// used to end with `return chart.config`, throwing the rest away, so the levels never reached the
// frontend even though every read already paid for them. Caching the envelope instead costs no extra
// request and no extra cache entry: `chartConfigQueryOptions` below projects the config back out with
// `select`, which changes what a COMPONENT receives without changing what the cache HOLDS.
//
// Shared so multiple consumers hit the SAME cache entry: the single-chart focus context AND the
// dashboard focus context, which loads every card's config reactively via useQueries.
export const chartEnvelopeQueryOptions = (company: string, chartId: ChartId | null) => ({
  queryKey: chartEnvelopeKey(company, chartId),
  enabled: Boolean(company && chartId),
  staleTime: 5 * 60 * 1000,
  ...FAILURE_IS_TERMINAL,
  queryFn: async (): Promise<DashboardChart> => {
    if (!chartId) throw new Error('chartId required'); // unreachable — `enabled` guards
    const res = await DashboardingLambdaFetch('/getChart', { params: { company, id: chartId } });
    return awaitJSON<DashboardChart>(res);
  }
});

// Load a saved chart's whole envelope: the config, plus `createdBy` and the two role levels that the
// permission gates read. Same cache entry as useChartConfig below — no second request.
export const useChartEnvelope = (company: string, chartId: ChartId | null) =>
  useQuery<DashboardChart>(chartEnvelopeQueryOptions(company, chartId));

// Query options that project the stored ChartConfig out of the cached envelope.
//
// KEEP THIS NAME on the projection. `AgentDrawer` does not call `useChartConfig` — it spreads this
// factory straight into `useQueries` and puts each result into a `Map<ChartId, ChartConfig>` that
// feeds the agent's focus context. Move the projection off this name and that map silently fills with
// envelopes: the agent reads the wrong shape and no code reports an error.
export const chartConfigQueryOptions = (company: string, chartId: ChartId | null) => ({
  ...chartEnvelopeQueryOptions(company, chartId),
  // Returns the SAME object reference the envelope already holds, so re-running this on a render
  // cannot churn a consumer's `data` identity.
  select: (chart: DashboardChart): ChartConfig => chart.config
});

// Load ONLY a saved chart's stored ChartConfig by id. A reactive source for the agent's focus context:
// an edit to an already-loaded chart must send that chart's CURRENT config, but the render cache
// (`useChartData`) is empty until the chart has drawn — so the focus context would otherwise fall back
// to no config and the agent rebuilds from scratch. This fetches the authoritative config straight from
// `/getChart`, regardless of whether it has rendered.
export const useChartConfig = (company: string, chartId: ChartId | null) =>
  useQuery<DashboardChart, Error, ChartConfig>(chartConfigQueryOptions(company, chartId));

// Load a saved chart for rendering (the single-chart view or a dashboard card): fetch the stored
// (company-agnostic) ChartConfig by id, then compile + run it for this company's data. Two hops
// because getChart returns only the config and getData renders it.
//
// Optional `overrides` adjust the saved config client-side (transient view picking) before the data
// is rendered: `dateRange` overrides the date range, `comparisonRange` the comparison-period window
// (period-over-period charts only), `kpiId` every KPI-using query's KPI. When omitted the chart uses
// its own saved values unchanged.
//
// Optional `baseConfig` is an UNSAVED working copy (the agent's proposed edit): when present, the GET
// /getChart is SKIPPED and the config is rendered directly, so the chart previews the edit without it
// ever being saved. One render path (the override layer + /getData below run identically over either
// source), not a parallel preview path. A `draft:<uuid>` handle has no saved row at all, so it MUST be
// passed a `baseConfig` (its working copy) — there is nothing to /getChart.
export const useChartData = (
  company: string,
  chartId: ChartHandle,
  overrides?: ChartDataOverrides,
  baseConfig?: ChartConfig
) => {
  const queryClient = useQueryClient();
  return useQuery<ChartWithData>({
    queryKey: buildChartDataQueryKey(company, chartId, overrides, baseConfig),
    // A draft handle has no saved row, so it renders ONLY from its working copy — without a baseConfig
    // (an orphaned draft: discarded elsewhere, or a stale/shared URL) there is nothing to fetch, and
    // firing /getChart for a draft:<uuid> id would be a doomed request. Enforce the hook's contract
    // here so the query never runs in that case (the caller shows a "no longer available" message).
    enabled: Boolean(company && chartId) && (Boolean(baseConfig) || !isDraftId(chartId)),
    // Keep the previously-rendered chart on screen while a new (company, chartId, overrides,
    // baseConfig) resolves — no spinner flash between edits, and (crucially) if a working copy fails
    // to render, the prior good preview stays visible instead of blanking (the caller shows an inline
    // "couldn't render that change" and lets the user retry or discard).
    placeholderData: keepPreviousData,
    // A chart's data for a fixed (company, chartId, overrides) is stable within a session, so keep
    // it fresh — otherwise (staleTime 0) re-subscribing to the same key triggers a wasted background
    // refetch. Notably this is what made unlinking a chart from the global date (same range, same
    // key) fire a redundant /getData call + flash.
    staleTime: 5 * 60 * 1000,
    ...FAILURE_IS_TERMINAL,
    queryFn: async () => {
      let config: ChartConfig;
      if (baseConfig) {
        config = baseConfig;
      } else if (isDraftId(chartId)) {
        throw new Error('draft chart requires a baseConfig'); // unreachable — `enabled` guards
      } else {
        // Shares the envelope cache entry; the render query's own retry covers a failure.
        const envelope = await queryClient.fetchQuery({
          ...chartEnvelopeQueryOptions(company, chartId),
          retry: false
        });
        config = envelope.config;
      }

      const { config: viewConfig, fetchConfig } = prepareChartRequest(config, overrides);
      config = viewConfig;

      const filterFields = dashboardFilterRequestFields(overrides);
      const dataRes = await DashboardingLambdaFetch('/getData', {
        method: 'POST',
        body: { company, config: fetchConfig, ...filterFields }
      });
      const data = await awaitJSON<QueryResponse>(dataRes);

      return { config, data, dashboardFilters: filterFields.dashboardFilters ?? [] };
    }
  });
};
